import { useEffect } from "react";
import {
  BUSINESS_NAME,
  EMAIL,
  PHONE_E164,
  SITE_URL_NORMALIZED,
  SOCIAL_LINKS,
  jsonLdAutoRepairProvider,
  jsonLdGeoCoordinates,
  jsonLdPostalAddress,
} from "@/lib/business";

interface ServiceSEO {
  title: string;
  description: string;
  canonical?: string;
}

export function useServiceMeta({ title, description, canonical }: ServiceSEO) {
  useEffect(() => {
    const prev = document.title;
    document.title = title;

    const setMeta = (name: string, content: string) => {
      let el = document.querySelector(`meta[name="${name}"]`) || document.querySelector(`meta[property="${name}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(name.startsWith("og:") || name.startsWith("twitter:") ? "property" : "name", name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };

    setMeta("description", description);
    setMeta("og:title", title);
    setMeta("og:description", description);
    setMeta("twitter:title", title);
    setMeta("twitter:description", description);

    return () => {
      document.title = prev;
    };
  }, [title, description, canonical]);
}

export function serviceJsonLd(opts: {
  name: string;
  description: string;
  url: string;
  priceRange?: string;
  offers?: { name: string; price: number; description?: string }[];
}) {
  return {
    "@context": "https://schema.org",
    "@type": "Service",
    name: opts.name,
    description: opts.description,
    url: opts.url,
    provider: jsonLdAutoRepairProvider(),
    areaServed: {
      "@type": "City",
      name: "Atlanta",
      sameAs: "https://en.wikipedia.org/wiki/Atlanta",
    },
    ...(opts.offers?.length
      ? {
          hasOfferCatalog: {
            "@type": "OfferCatalog",
            name: `${opts.name} Packages`,
            itemListElement: opts.offers.map((o) => ({
              "@type": "Offer",
              name: o.name,
              price: o.price,
              priceCurrency: "USD",
              ...(o.description ? { description: o.description } : {}),
            })),
          },
        }
      : {}),
  };
}

const baseUrl = SITE_URL_NORMALIZED;

export const LOCAL_BUSINESS_JSON_LD = {
  "@context": "https://schema.org",
  "@type": ["LocalBusiness", "AutoRepair"],
  "@id": `${baseUrl}/#business`,
  name: BUSINESS_NAME,
  url: baseUrl,
  telephone: PHONE_E164,
  email: EMAIL,
  priceRange: "$$–$$$",
  image: `${baseUrl}/og-image.jpg`,
  description:
    "Atlanta's premier auto detailing, ceramic coating, paint protection film (PPF), window tinting, and mobile vehicle maintenance studio. IDA-certified technicians, lifetime warranties, and showroom-quality results.",
  address: jsonLdPostalAddress(),
  geo: jsonLdGeoCoordinates(),
  areaServed: [
    { "@type": "City", name: "Atlanta" },
    { "@type": "City", name: "Buckhead" },
    { "@type": "City", name: "Midtown" },
    { "@type": "City", name: "Decatur" },
    { "@type": "City", name: "Sandy Springs" },
    { "@type": "City", name: "Roswell" },
    { "@type": "City", name: "Alpharetta" },
    { "@type": "City", name: "Marietta" },
    { "@type": "City", name: "Dunwoody" },
    { "@type": "City", name: "Brookhaven" },
    { "@type": "City", name: "Buford" },
    { "@type": "City", name: "Norcross" },
  ],
  openingHoursSpecification: [
    {
      "@type": "OpeningHoursSpecification",
      dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
      opens: "09:00",
      closes: "17:00",
    },
  ],
  hasOfferCatalog: {
    "@type": "OfferCatalog",
    name: "Auto Detailing Services",
    itemListElement: [
      { "@type": "Offer", itemOffered: { "@type": "Service", name: "Ceramic Coating", description: "Long-lasting paint protection with showroom-quality shine" } },
      { "@type": "Offer", itemOffered: { "@type": "Service", name: "Paint Protection Film (PPF)", description: "Self-healing invisible film that stops rock chips and scratches" } },
      { "@type": "Offer", itemOffered: { "@type": "Service", name: "Window Tinting", description: "Premium ceramic and color stable films with lifetime warranty" } },
      { "@type": "Offer", itemOffered: { "@type": "Service", name: "Auto Detailing & Paint Correction", description: "Interior and exterior deep cleaning plus multi-stage paint correction" } },
      { "@type": "Offer", itemOffered: { "@type": "Service", name: "Mobile Vehicle Maintenance", description: "Recurring wash and detail subscriptions delivered to your door" } },
    ],
  },
  sameAs: [...SOCIAL_LINKS],
};
